import { Button } from "@/atoms-react/button/Button.react";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react";
import InputLabel from "@/atoms-react/input/Input.react";

import { Base, Label, SectionTitle } from "@/atoms-react/text/Text.react";
import {
  AgentNatureMandate,
  LegalEntityLinkDto,
  MandateService,
  OlkyPassProLinkType
} from "@/features/company/types";
import { StaticMandateService, StaticNatureMandate } from "@/features/user/types";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { useAppSelector } from "@/redux/hooks";
import {
  staticLinkNatureRelation,
  StaticLinkNatureRelationEnum
} from "@/utils/static-data/nature-relation";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { RepresentativeConfiguration } from "../../AddCompanyModal/RepresentativeConfiguration";

type AddCompanyFormPrps = {
  mandateServices?: StaticMandateService[];
  onValidate: (data: LegalEntityLinkDto) => void;
  natureMandates?: StaticNatureMandate[];
  isRepresentative?: boolean;
  data?: LegalEntityLinkDto;
  isUpdatePending: boolean;
  onClose?: () => void;
  isAddForm: boolean;
  isLoadingNature: boolean;
  isPendingNatureMandates?: boolean;
};
export const EditCompanyRelatedLinkForm = ({
  onValidate,
  natureMandates,
  isRepresentative = false,
  data,
  isUpdatePending = false,
  isAddForm,
  isPendingNatureMandates
}: AddCompanyFormPrps) => {
  const { isRefetchTokenLoading } = useAppSelector((state) => state.token);
  const { t } = useTranslation(["pass"]);
  const labelInputDivClassName = "md:flex md:space-x-6";
  const labelClassName = "flex w-40 min-w-40 mb-2 md:mb-0";
  const inputClassName = "flex grow";

  const { getLanguage } = usei18n();

  const handleCheckboxChange = (
    natureChecked: string,
    isChecked: boolean,
    boRelations: StaticLinkNatureRelationEnum[]
  ) => {
    const newElement = isChecked
      ? boRelations
        ? [...boRelations, natureChecked]
        : [natureChecked]
      : boRelations.filter((nature) => nature !== natureChecked);
    return newElement;
  };
  const methods = useForm<LegalEntityLinkDto>({
    defaultValues: isRepresentative
      ? {
          ...data,
          mandatoryInformation: {
            linkType: isRepresentative
              ? OlkyPassProLinkType.AGENT
              : OlkyPassProLinkType.BENEFICIAL_OWNER,
            isCommunicationContact: data?.mandatoryInformation.isCommunicationContact ?? true,
            mandateServices: data?.mandatoryInformation?.mandateServices ?? [
              MandateService.ALIASES_MANAGEMENT
            ],
            agentNatureRelation: data?.mandatoryInformation.agentNatureRelation
              ? AgentNatureMandate[data?.mandatoryInformation.agentNatureRelation]
              : undefined,
            agentNatureRelationOther: data?.mandatoryInformation?.agentNatureRelationOther,
            isAgentLe: data?.mandatoryInformation?.isAgentLe ?? false,
            boRelationOther: data?.mandatoryInformation.boRelationOther,
            agentCompanyName: data?.mandatoryInformation.agentCompanyName,
            boPercentage: data?.mandatoryInformation.boPercentage,
            boRelations: data?.mandatoryInformation.boRelations
          }
        }
      : data
  });
  const {
    handleSubmit,
    control,
    formState: { errors: errors, isDirty },
    watch
  } = methods;
  const onSubmit: SubmitHandler<LegalEntityLinkDto> = (data) => {
    onValidate(data);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {isRepresentative ? (
          <div className="mt-5 flex flex-col justify-between border-2 border-slate-50 p-4 dark:border-slate-700">
            {!isAddForm ? (
              <div className="mb-10 flex w-full flex-row justify-center p-4">
                <SectionTitle>{t("business_links.add_link.title.edit_agent")}</SectionTitle>
              </div>
            ) : (
              <div className="mb-10 flex w-full flex-row justify-center px-6">
                <SectionTitle>{t("business_links.add_link.title.agent")}</SectionTitle>
              </div>
            )}
            <RepresentativeConfiguration
              natureMandates={natureMandates}
              pendingNatureMandates={isPendingNatureMandates}
              representative={data}
            />
            <div className="mt-8 flex w-full flex-row justify-center">
              <Button
                disabled={!isDirty}
                size="md"
                loading={isUpdatePending || isRefetchTokenLoading}
                type="submit"
                className="w-1/5 rounded-sm bg-primary"
                dataCy="submit-button"
              >
                {isAddForm ? t("business_links.button_3") : t("business_links.edit")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-5 flex flex-col justify-between border-2 border-slate-50 p-4 dark:border-slate-700">
            {!isAddForm ? (
              <div className="mb-10 flex w-full flex-row justify-center p-4">
                <SectionTitle>
                  {t("business_links.add_link.title.edit_representative")}
                </SectionTitle>
              </div>
            ) : (
              <div className="mb-10 flex w-full flex-row justify-center px-6">
                <SectionTitle>{t("business_links.add_link.title.representative")}</SectionTitle>
              </div>
            )}

            <div className={twMerge(labelInputDivClassName, "mt-2")}>
              <Label className={labelClassName}>
                {t("business_links.add_link.input.pourcentage")}
              </Label>
              <div className={inputClassName}>
                <Controller
                  control={control}
                  rules={{
                    required: true
                  }}
                  name="mandatoryInformation.boPercentage"
                  render={({ field: { onChange, value } }) => (
                    <div className="flex w-full flex-col">
                      <InputLabel
                        dataCy="input-bo-percentage"
                        type="number"
                        min={1}
                        max={100}
                        required={false}
                        value={value?.toString()}
                        suffixIcon={<Base className="font-bold">%</Base>}
                        error={
                          errors.mandatoryInformation?.boPercentage
                            ? t("atoms:general.required")
                            : ""
                        }
                        onChange={(value: string) => {
                          let response: string | null = null;
                          if (value != "") {
                            if (parseInt(value) < 0) {
                              response = `0`;
                            } else if (parseInt(value) >= 100) {
                              response = `100`;
                            } else response = value;
                          }
                          onChange(response);
                        }}
                      />
                    </div>
                  )}
                />
              </div>
            </div>

            <div className={twMerge(labelInputDivClassName, "mt-2")}>
              <Label className={twMerge("my-4 mt-8", labelClassName)}>
                <div className="flex flex-col">
                  {t("business_links.add_link.input.relation_type.title")}
                  <Label className="font-normal">
                    {t("business_links.add_link.input.relation_type.title_choice")}
                  </Label>
                </div>
                {/* TODO : tooltip text needs to be validate
                    <ExclamationCircleIcon
                      className="ml-2 h-7 w-7 text-pblue-500"
                      data-tooltip={t("business_links.add_link.input.relation_type.tooltip")}
                    /> 
                  */}
              </Label>
              <div className="flex flex-col">
                <Controller
                  control={control}
                  rules={{
                    required: true
                  }}
                  name="mandatoryInformation.boRelations"
                  render={({ field: { onChange, value } }) => (
                    <div
                      className={
                        "my-4 mt-8 mb-2 grid w-full min-w-40 grid-cols-1 gap-2 md:mb-0 md:grid-cols-2"
                      }
                    >
                      {Object.entries(staticLinkNatureRelation).map(([key, valueItem]) => (
                        <Checkbox
                          dataCy={key}
                          key={key}
                          value={value?.some((nature: string) => nature === key)}
                          className={errors.mandatoryInformation?.boRelations && "border-red-500"}
                          onChange={(v) => {
                            const arr = handleCheckboxChange(key, v, value ?? []);
                            onChange(arr);
                          }}
                        >
                          <Base className="mb-1 text-black">
                            {getLanguage() === "fr" ? valueItem.content_fr : valueItem.content_en}
                          </Base>
                        </Checkbox>
                      ))}
                    </div>
                  )}
                />

                <Controller
                  control={control}
                  rules={{
                    required: watch("mandatoryInformation.boRelations")?.includes(
                      StaticLinkNatureRelationEnum.OTHER
                    )
                  }}
                  name="mandatoryInformation.boRelationOther"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <>
                        {watch("mandatoryInformation.boRelations")?.includes(
                          StaticLinkNatureRelationEnum.OTHER
                        ) && (
                          <>
                            <InputLabel
                              dataCy="company-create-mandatary-nature-mandate-other-input"
                              placeholder={t(
                                "business_links.add_link.input.relationTypeOther.placeholder"
                              )}
                              required={watch("mandatoryInformation.boRelations")?.includes(
                                StaticLinkNatureRelationEnum.OTHER
                              )}
                              noLatinCheck={true}
                              className="mt-2 w-full"
                              error={
                                errors.mandatoryInformation?.boRelationOther
                                  ? t("business_links.add_link.input.relationTypeOther.error")
                                  : ""
                              }
                              value={value ?? ""}
                              onChange={onChange}
                            />
                          </>
                        )}
                      </>
                    );
                  }}
                />
              </div>
            </div>
            <div className="mt-8 flex w-full flex-row justify-center">
              <Button
                disabled={!isDirty}
                size="md"
                loading={isUpdatePending || isRefetchTokenLoading}
                type="submit"
                className="w-1/5 rounded-sm bg-primary"
                dataCy="submit-button"
              >
                {isAddForm ? t("business_links.button_3") : t("business_links.edit")}
              </Button>
            </div>
          </div>
        )}
      </form>
    </FormProvider>
  );
};
